import React from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { IStorefrontSettings, buildStorefrontPatch } from '../../../../models/Storefront';
import {
    useGetStorefrontQuery,
    usePatchStorefrontMutation,
} from '../../../../store/endpoints/StorefrontSettingsEndpoints';
import { toast } from 'react-toastify';
import StorefrontSettingsTitleBar from '../../../../components/Storefront/StorefrontSettingsTitleBar';
import useStorefrontEditLease from '../../../../hooks/useStorefrontEditLease';
import { StorefrontReadOnlyRegion } from '../../../../components/Storefront/StorefrontEditLock';
import { storefrontSaveErrorMessage } from '../../../../logic/storefrontLeaseConflict';
import StorefrontLocalizationEditor from '../../../../components/Storefront/Editor/StorefrontLocalizationEditor';

interface StorefrontLocalizationPageParams {
    storefront: string;
}

// Localization overrides change on every keystroke, so save is debounced (and flushed on blur /
// unmount) rather than on an explicit button — mirroring the General Settings page.
const DEBOUNCE_MS = 1200;

const StorefrontLocalizationPage: React.FC<RouteComponentProps<StorefrontLocalizationPageParams>> = (props) => {
    const { storefront } = props.match.params;
    const storefrontQuery = useGetStorefrontQuery(storefront, { skip: !storefront });
    const [patchStorefront] = usePatchStorefrontMutation();

    const [localSettings, setLocalSettingsRaw] = React.useState<IStorefrontSettings | undefined>(undefined);
    const localSettingsRef = React.useRef<IStorefrontSettings | undefined>();
    const originalSettingsRef = React.useRef<IStorefrontSettings | undefined>();
    const debounceSaveRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
    // Overrides autosave on a debounce, so a lease held by an assistant turn would turn a burst of
    // typing into a burst of rejected patches. One flag switches the editor off instead.
    const { locked: readOnly } = useStorefrontEditLease(storefront);

    const setLocalSettings = React.useCallback((s: IStorefrontSettings) => {
        localSettingsRef.current = s;
        setLocalSettingsRaw(s);
    }, []);

    React.useEffect(() => {
        if (storefrontQuery.data && !originalSettingsRef.current) {
            setLocalSettings(storefrontQuery.data);
            originalSettingsRef.current = storefrontQuery.data;
        }
    }, [storefrontQuery.data, setLocalSettings]);

    const handleSettingsChanged = React.useCallback(() => {
        originalSettingsRef.current = undefined;
    }, []);

    const doSave = React.useCallback(async (current: IStorefrontSettings) => {
        if (!originalSettingsRef.current) return;
        const patch = buildStorefrontPatch(originalSettingsRef.current, current);
        if (Object.keys(patch).length === 0) return;
        try {
            await patchStorefront({ id: storefront, patch }).unwrap();
            originalSettingsRef.current = { ...originalSettingsRef.current, ...patch };
            toast.success('Localization saved');
        } catch (e) {
            toast.error(storefrontSaveErrorMessage(e, 'Failed to save localization'));
        }
    }, [patchStorefront, storefront]);

    const scheduleSave = React.useCallback(() => {
        if (debounceSaveRef.current) clearTimeout(debounceSaveRef.current);
        debounceSaveRef.current = setTimeout(() => {
            if (localSettingsRef.current) doSave(localSettingsRef.current);
        }, DEBOUNCE_MS);
    }, [doSave]);

    // Flush a pending debounced save when navigating away.
    React.useEffect(() => {
        return () => {
            if (debounceSaveRef.current) {
                clearTimeout(debounceSaveRef.current);
                if (localSettingsRef.current && originalSettingsRef.current) {
                    const patch = buildStorefrontPatch(originalSettingsRef.current, localSettingsRef.current);
                    if (Object.keys(patch).length > 0) {
                        patchStorefront({ id: storefront, patch });
                    }
                }
            }
        };
    }, [patchStorefront, storefront]);

    if (storefrontQuery.isLoading || !localSettings) {
        return <div>Loading...</div>;
    }

    return (
        <div>
            <StorefrontSettingsTitleBar
                storefront={storefront}
                title='Localization'
                onSettingsChanged={handleSettingsChanged}
            />
            <StorefrontReadOnlyRegion readOnly={readOnly}>
                <StorefrontLocalizationEditor
                    storefrontSettings={localSettings}
                    onStorefrontSettingsChange={(updated) => {
                        setLocalSettings(updated);
                        scheduleSave();
                    }}
                />
            </StorefrontReadOnlyRegion>
        </div>
    );
};

export default StorefrontLocalizationPage;
